import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Event } from './eventsTypes';

// ✅ Enhanced State Interface
interface EventFilters {
  search: string;
  status: 'all' | 'draft' | 'scheduled' | 'completed' | 'live' | 'cancelled' | 'postponed';
  category_id?: string;
  venue_id?: string;
  sortBy: 'createdAt' | 'start_date' | 'title' | 'view_count' | 'attended_count';
  sortOrder: 'asc' | 'desc';
  page: number;
  limit: number;
  city?: string;
  dateRange: string ; // Support both formats
  priceRange: string ; // Support both formats
}

interface EventsState {
  // ✅ Data
  currentEvent: Event | null;
  events: Event[];
  categories: any[];
  venues: any[];
  
  // ✅ UI State
  filters: EventFilters;
  selectedEventId: string | null;
  
  // ✅ Loading States
  loading: boolean;
  error: string | null;
  
  // ✅ Pagination
  total: number;
  hasMore: boolean;
  
  // ✅ View Mode (for EventsPage)
  viewMode: 'grid' | 'list';
  
  // ✅ Modal States
  isCreateModalOpen: boolean;
  isEditModalOpen: boolean;
  isDeleteModalOpen: boolean;
  eventToDelete: string | null;
}

// ✅ Initial State
const initialState: EventsState = {
  // Data
  currentEvent: null,
  events: [],
  categories: [],
  venues: [],
  
  // Filters
  filters: {
    search: '',
    status: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    page: 1,
    limit: 12,
    dateRange: 'all',
    priceRange: 'all'
  },
  
  // UI State
  selectedEventId: null,
  viewMode: 'grid',
  
  // Loading
  loading: false,
  error: null,
  
  // Pagination
  total: 0,
  hasMore: true,
  
  // Modals
  isCreateModalOpen: false,
  isEditModalOpen: false,
  isDeleteModalOpen: false,
  eventToDelete: null,
};

// ✅ Slice
const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    // ✅ Event Selection
    setCurrentEvent: (state, action: PayloadAction<Event | null>) => {
      state.currentEvent = action.payload;
      state.selectedEventId = action.payload?.documentId || null;
    },
    
    setSelectedEventId: (state, action: PayloadAction<string | null>) => {
      state.selectedEventId = action.payload;
    },
    
    // ✅ Filter Management
    setFilters: (state, action: PayloadAction<Partial<EventFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      
      // Reset to page 1 when filters change (except pagination)
      if (action.payload.page === undefined) {
        state.filters.page = 1;
      }
    },
    
    setSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
      state.filters.page = 1; // Reset pagination
    },
    
    setStatus: (state, action: PayloadAction<EventFilters['status']>) => {
      state.filters.status = action.payload;
      state.filters.page = 1;
    },
    
    setCategory: (state, action: PayloadAction<string | undefined>) => {
      state.filters.category_id = action.payload;
      state.filters.page = 1;
    },
    setCity: (state, action: PayloadAction<string | undefined>) => {
      state.filters.city = action.payload;
      state.filters.page = 1;
    },
    setDateRange: (state, action: PayloadAction<string | undefined>) => {
      state.filters.dateRange = action.payload;
      state.filters.page = 1;
    },
    setPriceRange: (state, action: PayloadAction<string | undefined>) => {
      state.filters.priceRange = action.payload;
      state.filters.page = 1;
    },
    setVenue: (state, action: PayloadAction<string | undefined>) => {
      state.filters.venue_id = action.payload;
      state.filters.page = 1;
    },
    
    setSortBy: (state, action: PayloadAction<EventFilters['sortBy']>) => {
      state.filters.sortBy = action.payload;
    },
    
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.filters.sortOrder = action.payload;
    },
    
    setPage: (state, action: PayloadAction<number>) => {
      state.filters.page = action.payload;
    },
    
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    
    // ✅ View Mode
    setViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.viewMode = action.payload;
    },
    
    // ✅ Modal Management
    openCreateModal: (state) => {
      state.isCreateModalOpen = true;
    },
    
    closeCreateModal: (state) => {
      state.isCreateModalOpen = false;
    },
    
    openEditModal: (state, action: PayloadAction<string>) => {
      state.isEditModalOpen = true;
      state.selectedEventId = action.payload;
    },
    
    closeEditModal: (state) => {
      state.isEditModalOpen = false;
      state.selectedEventId = null;
    },
    
    openDeleteModal: (state, action: PayloadAction<string>) => {
      state.isDeleteModalOpen = true;
      state.eventToDelete = action.payload;
    },
    
    closeDeleteModal: (state) => {
      state.isDeleteModalOpen = false;
      state.eventToDelete = null;
    },
    
    // ✅ Error Handling
    clearEventsError: (state) => {
      state.error = null;
    },
    
    // ✅ Manual Data Updates (for optimistic updates)
    addEvent: (state, action: PayloadAction<Event>) => {
      state.events.unshift(action.payload);
      state.total += 1;
    },
    
    updateEvent: (state, action: PayloadAction<Event>) => {
      const index = state.events.findIndex(e => e.documentId === action.payload.documentId);
      if (index !== -1) {
        state.events[index] = action.payload;
      }
      if (state.currentEvent?.documentId === action.payload.documentId) {
        state.currentEvent = action.payload;
      }
    },
    
    removeEvent: (state, action: PayloadAction<string>) => {
      state.events = state.events.filter(e => e.documentId !== action.payload);
      state.total -= 1;
      if (state.currentEvent?.documentId === action.payload) {
        state.currentEvent = null;
      }
    },
    // Add to your existing slice
incrementPage: (state) => {
  state.filters.page += 1;
},

  },
});

// ✅ Actions Export
export const {
  // Selection
  setCurrentEvent,
  setSelectedEventId,
  
  // Filters
  setFilters,
  setSearch,
  setStatus,
  setCategory,
  setVenue,
  setSortBy,
  setSortOrder,
  setPage,
  resetFilters,
  setCity,
  setDateRange,
  setPriceRange,
  // View
  setViewMode,
  
  // Modals
  openCreateModal,
  closeCreateModal,
  openEditModal,
  closeEditModal,
  openDeleteModal,
  closeDeleteModal,
  
  // Error
  clearEventsError,
  incrementPage,
  // Manual Updates
  addEvent,
  updateEvent,
  removeEvent,
} = eventsSlice.actions;

// ✅ Reducer Export
export default eventsSlice.reducer;

// ✅ SELECTORS (Bonus for easy access)
export const selectEventFilters = (state: { events: EventsState }) => state.events.filters;
export const selectCurrentEvent = (state: { events: EventsState }) => state.events.currentEvent;
export const selectSelectedEventId = (state: { events: EventsState }) => state.events.selectedEventId;
export const selectViewMode = (state: { events: EventsState }) => state.events.viewMode;
export const selectEventsLoading = (state: { events: EventsState }) => state.events.loading;
export const selectEventsError = (state: { events: EventsState }) => state.events.error;
